import { getName } from "@home/utils/common";
import React, { useEffect, useRef, useState } from "react";
import { ConfigProvider } from "tea-component";
import { useFilterData } from "./_hooks";
import classNames from "classnames";
import Abnormal from "@home/components/Abnormal";
import VersionFilter from "./VersionFilter";
import { IGTDRFilterProps } from "./_type";
import { useLocal } from "@/pages/dashboard/utils/customHook";
import Render from "@/pages/dashboard/components/PageContainer/Render";
import './index.scss';
import CommonCheckBox from "@/pages/dashboard/components/CommonCheckBox";
import { BENCHMARK_RATE_DEFAULT, CHECKLIST_DEFAULT } from "./VersionFilter/_constant";
import { dynamicString } from "@home/utils/dynamicString";
import { useProps } from "@/pages/dashboard/components/PageContainer/Render/hooks";

/**
 * GTDRAssessmentFilter 组件用于渲染 GTDR 评估过滤器界面
 * @param props - 组件属性
 * @param props.children - 子组件
 * @param props.className - 自定义类名
 * @param props.style - 自定义样式
 * @param props.versionConfig - 版本筛选器配置
 * @param props.versionConfig.versionNameEn - 版本筛选器英文名称
 * @param props.versionConfig.versionNameCn - 版本筛选器中文名称
 * @param props.versionConfig.versionErrorTipsEn - 版本选择错误提示（英文）
 * @param props.versionConfig.versionErrorTipsCn - 版本选择错误提示（中文）
 * @param props.versionConfig.versionTipsCn - 版本提示信息（中文）
 * @param props.versionConfig.versionTipsEn - 版本提示信息（英文）
 * @param props.versionConfig.versionDefault - 默认选中的版本
 * @param props.versionConfig.minSelectedNum - 最少选择数量
 * @param props.api - API 配置
 * @returns 返回带有版本筛选功能的 GTDR 评估过滤器组件
 */
export default function GTDRAssessmentFilter(props: IGTDRFilterProps) {
  const { children, className, style, versionConfig, checkBoxConfig, api, system, config } = props;
  const { lastUpdated } = config || {};
  // @ts-ignore
  const { data: usePropsData = {} } = useProps(props);

  const {
    versionNameEn = 'Version',
    versionNameCn = '版本',
    versionErrorTipsEn = 'Please select a version',
    versionErrorTipsCn = '请选择一个版本',
    versionTipsCn = '',
    versionTipsEn = '',
    versionDefault,
    minSelectedNum = 1,
  } = versionConfig || {};


  const { 
    value: benchmarkLevelDefault, 
    checkList = CHECKLIST_DEFAULT
  } = checkBoxConfig || {};

  const { locale, isEN } = useLocal('filter');
  // const [version, setVersion] = useState<string[]>([]);
  const [showBubble, setShowBubble] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<any>({
    version: versionDefault,
  });

  const [benchmarkLevel, setBenchmarkLevel] = useState(benchmarkLevelDefault);

  // 添加filterValueRef 是因为当除非筛选器错误提示（少于最少选中数）的时候，不希望触发页面的接口重新请求
  const filterValueRef = useRef({
    version: versionDefault,
  });

  const {
    loading,
    error,
    selector,
    versionOptions,
    // gameListMap,
    gameCode,
    gameName,
    gameCover,
    latestVersionObj,
    studioCode
  } = useFilterData(isEN, system, api);

  useEffect(() => {
    // 配置>代码计算的最新
    const versionDefaultVal = versionDefault || [latestVersionObj?.version] || [];
    // setVersion(versionDefaultVal);
    setFilterValue({
      version: versionDefaultVal,
    });
    filterValueRef.current = {
      version: versionDefaultVal,
    };
    setBenchmarkLevel(benchmarkLevelDefault || BENCHMARK_RATE_DEFAULT);
  }, [versionDefault, latestVersionObj?.version, benchmarkLevelDefault]);


  /**
   * 处理确认按钮点击事件
   * 当选择的版本数量满足最小要求时，更新过滤值
   * @private
   */
  const handleCofirm = (optionsValue: string[]) => {
    if (optionsValue?.length < minSelectedNum) {
      setShowBubble(true);
      setFilterValue({
        version: [],
      });
    } else {
      setShowBubble(false);
      setFilterValue({
        version: optionsValue,
      });
      filterValueRef.current = {
        version: optionsValue,
      };
    }
  };

  const handleOnChange = (val) => {
    setBenchmarkLevel(val);
  };

  return (
    <ConfigProvider locale={isEN ? 'zh' : 'en'}>
      <div className={classNames('dbd-GTDR-filter', className)} style={style ? style : {}}>
        <Abnormal
          isLoading={loading}
          isError={error}
          classNames={"dbd-GTDR-filter-abnormal"}
          // 733 为保持和外部loading高度一致（页面配置获取的loading）
          style={{ height: '36px', backgroundColor: '#fff', marginBottom: '20px' }}
          component={
            <div className='dbd-GTDR-filter-box'>
              <VersionFilter
                value={filterValue?.version || []}
                options={versionOptions}
                // onChange={onChange}
                locale={locale}
                showBubble={showBubble}
                bubbleContent={getName(isEN, versionErrorTipsEn, versionErrorTipsCn)}
                name={getName(isEN, versionNameEn, versionNameCn)}
                tooltip={getName(isEN, versionTipsEn, versionTipsCn)}
                onConfirm={handleCofirm}
                isRevertToLastSelection={true}
                isEN={isEN}
                buttonDesName={getName(isEN, latestVersionObj?.description_en, latestVersionObj?.description_cn)}
              />
              <CommonCheckBox
                className='dbd-GTDR-filter-check'
                value={benchmarkLevel}
                checkList={checkList}
                onChange={handleOnChange} />
              {lastUpdated?.nameEn && (
                <div className='dbd-GTDR-filter-last-updated'>
                  {dynamicString(getName(isEN, lastUpdated?.nameEn, lastUpdated?.nameCn), usePropsData)}
                </div>
              )}
            </div>
          }
        />
        {
          children?.map((item, index) => {
            const { config, data, changeData } = item?.props || {};
            return <>
              <Render
                config={config}
                key={item?.name + index}
                data={data}
                changeData={changeData}
                filterValue={filterValueRef.current}
                benchmarkLevel={benchmarkLevel}
                checkList={checkList}
                filterLoading={loading}
                filterError={error}
                selector={selector}
                // gameListMap={gameListMap}
                // 父组件处理，防止studio和经分gamecode不一样，导致子组件代码逻辑修改
                latestVersionObj={latestVersionObj}
                gameCode={gameCode}
                gameName={gameName}
                gameCover={gameCover}
                studioCode={studioCode}
              />
            </>;
          })
        }
      </div>
    </ConfigProvider>
  );
}
